import { useEffect, useRef, useCallback } from "react";
import type { UIMessage } from "../../types/chat";
import type { ToolAnimationState } from "../../hooks/useToolAnimation";
import { useAuthStore } from "../../stores/auth-store";
import { UserMessageBubble } from "./UserMessageBubble";
import { AIMessageBubble } from "./AIMessageBubble";
import { ScoutingIndicator } from "./ScoutingIndicator";

interface MessageListProps {
  messages: UIMessage[];
  onRetry?: () => void;
  toolAnimation?: ToolAnimationState;
}

export function MessageList({
  messages,
  onRetry,
  toolAnimation,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const user = useAuthStore((s) => s.user);
  const userName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "You";
  const avatarUrl = user?.avatar_url;

  // Find the index of the last assistant error message
  let lastErrorIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant" && messages[i].status === "error") {
      lastErrorIndex = i;
      break;
    }
  }

  // Track whether user is near the bottom of the scroll container
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 150;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  // Discrete events: new message added, tool animation toggle -> always smooth scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, toolAnimation?.active]);

  // Streaming: scroll as content grows, but only if user is near bottom
  const lastMessage = messages[messages.length - 1];
  const lastContent = lastMessage?.content;
  const isLastStreaming = lastMessage?.status === "streaming";
  useEffect(() => {
    if (isLastStreaming && isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "instant" });
    }
  }, [lastContent, isLastStreaming]);

  // Pin to bottom when content grows (e.g. GIF/image loads, streaming tokens arrive)
  // so the animal animation doesn't get clipped behind the input area. We compute
  // near-bottom live here (not from the ref) because the ref can be stale mid
  // smooth-scroll, and we use a generous threshold so content growing past the
  // viewport while a scroll is in flight still re-pins.
  useEffect(() => {
    const content = contentRef.current;
    const container = scrollContainerRef.current;
    if (!content || !container) return;
    const observer = new ResizeObserver(() => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 600) {
        container.scrollTop = container.scrollHeight;
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const toolLabel = toolAnimation?.active ? toolAnimation.label : undefined;

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-1 py-2"
    >
      <div ref={contentRef} className="space-y-4 py-4">
        {messages.map((msg, index) => {
          // Only animate the last 2 messages to avoid delaying entire history
          const shouldAnimate = index >= messages.length - 2;

          // Empty streaming placeholder -> centered scouting animal (v3 isTyping)
          const isThinking =
            msg.role === "assistant" &&
            msg.status === "streaming" &&
            msg.content === "";

          return (
            <div
              key={msg.id}
              className={
                shouldAnimate && !isThinking
                  ? "motion-safe:animate-message-in"
                  : undefined
              }
              style={
                shouldAnimate && !isThinking
                  ? { animationFillMode: "both" }
                  : undefined
              }
            >
              {isThinking ? (
                <ScoutingIndicator label={toolLabel} />
              ) : msg.role === "user" ? (
                <UserMessageBubble
                  message={msg}
                  userName={userName}
                  avatarUrl={avatarUrl}
                />
              ) : (
                <AIMessageBubble
                  message={msg}
                  onRetry={index === lastErrorIndex ? onRetry : undefined}
                />
              )}
            </div>
          );
        })}

        {/* Tool called mid-stream (content already flowing): animal below the messages */}
        {toolAnimation?.active &&
          messages[messages.length - 1]?.content !== "" && (
            <ScoutingIndicator label={toolAnimation.label} />
          )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
